import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOrganization } from '@clerk/clerk-react';
import {
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  FileSearch,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { createPortalOutboundCreateService } from '@/services/portalio/outboundCreateService';
import { createPortalOutboundPreviewService } from '@/services/portalio/outboundPreviewService';
import type {
  PortalOutboundCreateResult,
  PortalOutboundItemStatus,
  PortalOutboundPortalUser,
  PortalOutboundPreview,
} from '@/types/portalOutbound';

interface PortalOutboundPreviewDialogProps {
  floorPlanId: string;
}

interface PortalSalesPersonFallback {
  id: string;
  name: string | null;
}

function statusBadgeVariant(status: PortalOutboundItemStatus) {
  if (status === 'exportable') return 'success';
  if (status === 'ai_fallback') return 'warning';
  return 'destructive';
}

function statusLabel(status: PortalOutboundItemStatus) {
  if (status === 'exportable') return 'Portal match';
  if (status === 'ai_fallback') return 'AI fallback';
  return 'Blocked';
}

function StatusIcon({ status }: { status: PortalOutboundItemStatus }) {
  if (status === 'exportable') return <CheckCircle2 className="h-3.5 w-3.5" />;
  if (status === 'ai_fallback') return <Sparkles className="h-3.5 w-3.5" />;
  return <AlertCircle className="h-3.5 w-3.5" />;
}

function getLocalPortalSalesPersonFallback(orgId: string): PortalSalesPersonFallback | null {
  const raw = window.localStorage.getItem(`portal-salesperson-fallback:${orgId}`);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as { id?: unknown; name?: unknown };
    if (typeof parsed.id !== 'string' || parsed.id.trim().length === 0) return null;

    return { id: parsed.id.trim(), name: typeof parsed.name === 'string' ? parsed.name : null };
  } catch {
    return null;
  }
}

export function PortalOutboundPreviewDialog({ floorPlanId }: PortalOutboundPreviewDialogProps) {
  const { organization } = useOrganization();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [preview, setPreview] = useState<PortalOutboundPreview | null>(null);
  const [portalUsers, setPortalUsers] = useState<PortalOutboundPortalUser[]>([]);
  const [authUserFallback, setAuthUserFallback] = useState<PortalSalesPersonFallback | null>(null);
  const [selectedSalesPersonId, setSelectedSalesPersonId] = useState('');
  const [manualSalesPersonId, setManualSalesPersonId] = useState('');
  const [userLoadError, setUserLoadError] = useState<string | null>(null);
  const [isTestingUsersEndpoint, setIsTestingUsersEndpoint] = useState(false);
  const [usersEndpointResult, setUsersEndpointResult] = useState<string | null>(null);
  const [proposalName, setProposalName] = useState('');
  const [createResult, setCreateResult] = useState<PortalOutboundCreateResult | null>(null);

  const service = useMemo(() => createPortalOutboundPreviewService(), []);
  const createService = useMemo(() => createPortalOutboundCreateService(), []);

  const applySalesPersonFallback = useCallback((user: PortalOutboundPortalUser) => {
    setPortalUsers([user]);
    setSelectedSalesPersonId(String(user.id));
    setUserLoadError(null);
  }, []);

  const loadPortalUserFallback = useCallback(async () => {
    if (!organization?.id || portalUsers.length > 0) return;

    const storedUser = await createService.getStoredPortalUser(organization.id);
    if (storedUser) {
      applySalesPersonFallback(storedUser);
      setAuthUserFallback({
        id: String(storedUser.id),
        name:
          [storedUser.firstName, storedUser.lastName].filter(Boolean).join(' ') ||
          storedUser.email ||
          null,
      });
      return;
    }

    const localUser = getLocalPortalSalesPersonFallback(organization.id);
    if (!localUser) {
      setUserLoadError(
        'Portal blocks the users lookup for this API key. Enter a numeric Portal SalesPersonId manually.',
      );
      return;
    }

    setAuthUserFallback(localUser);
    const numericAuthUserId = Number(localUser.id);

    if (Number.isInteger(numericAuthUserId) && numericAuthUserId > 0) {
      const user = {
        id: numericAuthUserId,
        firstName: localUser.name || 'Connected Portal user',
        lastName: null,
        email: null,
        permissionGroup: 'Authenticated Portal user',
      };
      applySalesPersonFallback(user);
      return;
    }

    setUserLoadError(
      `Portal authenticated as ${localUser.name || localUser.id}, but proposal creation needs the numeric SalesPersonId. Enter it manually.`,
    );
  }, [applySalesPersonFallback, createService, organization?.id, portalUsers.length]);

  const loadPreview = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await service.buildPreview(floorPlanId);
      setPreview(result);
      setProposalName(currentName => currentName || `${result.project.name} - vfloow export`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to build Portal export preview');
    } finally {
      setIsLoading(false);
    }
  }, [floorPlanId, service]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (nextOpen && !preview && !isLoading) {
        void loadPreview();
      }
      if (nextOpen) {
        void loadPortalUserFallback();
      }
    },
    [isLoading, loadPortalUserFallback, loadPreview, preview],
  );

  useEffect(() => {
    if (open && organization?.id && portalUsers.length === 0 && !userLoadError) {
      void loadPortalUserFallback();
    }
  }, [loadPortalUserFallback, open, organization?.id, portalUsers.length, userLoadError]);

  const effectiveSalesPersonId = manualSalesPersonId.trim() || selectedSalesPersonId;
  const numericSalesPersonId = Number(effectiveSalesPersonId);
  const hasValidSalesPersonId = Number.isInteger(numericSalesPersonId) && numericSalesPersonId > 0;

  const handleCreateProposal = useCallback(async () => {
    if (!organization?.id || !preview) return;

    setIsCreating(true);
    setCreateResult(null);
    try {
      const result = await createService.createProposalFromFloorPlan(floorPlanId, {
        orgId: organization.id,
        proposalName,
        salesPersonId: numericSalesPersonId,
      });
      setCreateResult(result);
      if (result.aiBuilder?.observedLineItems && result.aiBuilder.observedLineItems > 0) {
        toast.success(
          `Portal proposal ${result.proposalId} created with ${result.aiBuilder.observedLineItems} line item${result.aiBuilder.observedLineItems === 1 ? '' : 's'}`,
        );
      } else if (result.aiBuilder?.warning) {
        toast.warning(`Portal proposal ${result.proposalId} created; AI Builder needs review`);
      } else if (result.aiBuilder?.buildQueued) {
        toast.success(`Portal proposal ${result.proposalId} created and AI Builder queued`);
      } else {
        toast.success(`Portal proposal ${result.proposalId} created`);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create Portal proposal');
    } finally {
      setIsCreating(false);
    }
  }, [createService, floorPlanId, numericSalesPersonId, organization?.id, preview, proposalName]);

  const handleTestUsersEndpoint = useCallback(async () => {
    if (!organization?.id) return;

    setIsTestingUsersEndpoint(true);
    setUsersEndpointResult(null);
    try {
      const users = await createService.listPortalUsers(organization.id);
      setPortalUsers(users);
      setUserLoadError(null);

      if (users.length === 1) {
        setSelectedSalesPersonId(String(users[0].id));
      }

      const message = `Loaded ${users.length} Portal user${users.length === 1 ? '' : 's'}.`;
      setUsersEndpointResult(message);
      toast.success(message);
    } catch {
      setUserLoadError(
        "Couldn't load Portal users. Enter a numeric Portal SalesPersonId manually.",
      );
      setUsersEndpointResult(
        "Couldn't load Portal users — you can still enter the SalesPersonId manually.",
      );
      toast.error("Couldn't load Portal users");
    } finally {
      setIsTestingUsersEndpoint(false);
    }
  }, [createService, organization?.id]);

  const createDisabledReason = (() => {
    if (!organization?.id) return 'Organization is still loading';
    if (!preview) return 'Preview is still loading';
    if (preview.counts.exportable + preview.counts.aiFallback === 0)
      return 'At least one Portal-matched or AI-ready product is required';
    if (preview.counts.blocked > 0)
      return 'Resolve blocked items before creating a Portal proposal';
    if (!proposalName.trim()) return 'Proposal name is required';
    if (!hasValidSalesPersonId) return 'Enter a numeric Portal SalesPersonId';
    if (isCreating) return 'Portal proposal is being created';
    return null;
  })();

  const canCreate = !createDisabledReason;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-8">
          <FileSearch className="h-4 w-4 mr-1" />
          <span className="text-xs">Portal Preview</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="flex max-h-[90vh] max-w-5xl flex-col overflow-hidden p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>Portal proposal export preview</DialogTitle>
          <DialogDescription>
            Validate and create a Portal draft from this vfloow project.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-4">
          {isLoading && (
            <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Building preview
            </div>
          )}

          {!isLoading && preview && (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                <div className="rounded-md border p-3">
                  <p className="text-xs text-muted-foreground">Project</p>
                  <p className="truncate text-sm font-medium">{preview.project.name}</p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-xs text-muted-foreground">Areas</p>
                  <p className="text-sm font-medium">{preview.counts.areas}</p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-xs text-muted-foreground">Portal matched</p>
                  <p className="text-sm font-medium">{preview.counts.exportable}</p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-xs text-muted-foreground">AI fallback</p>
                  <p className="text-sm font-medium">{preview.counts.aiFallback}</p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-xs text-muted-foreground">Blocked</p>
                  <p className="text-sm font-medium">{preview.counts.blocked}</p>
                </div>
              </div>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Experimental AI Builder create behavior</AlertTitle>
                <AlertDescription>
                  This creates a real Portal draft, creates Portal areas from vfloow rooms, uploads
                  a structured vfloow bill of materials to Portal AI Builder, and attempts to queue
                  the Portal build. No internal notes are written automatically.
                </AlertDescription>
              </Alert>

              {preview.issues.length > 0 && (
                <div className="space-y-2 rounded-md border bg-muted/30 p-3">
                  {preview.issues.map(issue => (
                    <div key={issue.message} className="flex items-start gap-2 text-sm">
                      <AlertCircle className="mt-0.5 h-4 w-4 text-muted-foreground" />
                      <span>{issue.message}</span>
                    </div>
                  ))}
                </div>
              )}

              <ScrollArea className="h-[420px] rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[180px]">Status</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead>Area</TableHead>
                      <TableHead className="w-[80px] text-right">Qty</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.items.map(item => (
                      <TableRow key={item.key}>
                        <TableCell>
                          <Badge
                            variant={statusBadgeVariant(item.status)}
                            className="gap-1 whitespace-nowrap"
                          >
                            <StatusIcon status={item.status} />
                            {statusLabel(item.status)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <p className="font-medium">{item.displayName}</p>
                            <p className="text-xs text-muted-foreground">{item.reason}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <p>{item.areaName}</p>
                            <p className="text-xs text-muted-foreground">{item.floorPlanName}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                      </TableRow>
                    ))}
                    {preview.items.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="h-32 text-center text-muted-foreground">
                          No placed products found for this project.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>

              <div className="grid gap-4 rounded-md border p-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="portal-proposal-name">Proposal name</Label>
                  <Input
                    id="portal-proposal-name"
                    value={proposalName}
                    onChange={event => setProposalName(event.target.value)}
                    placeholder="Portal proposal name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Salesperson</Label>
                  <div className="space-y-2">
                    {portalUsers.length > 0 && (
                      <Select
                        value={selectedSalesPersonId}
                        onValueChange={setSelectedSalesPersonId}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select salesperson" />
                        </SelectTrigger>
                        <SelectContent>
                          {portalUsers.map(user => {
                            const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
                            return (
                              <SelectItem key={user.id} value={String(user.id)}>
                                {name || user.email || `Portal user ${user.id}`}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="portal-salesperson-id" className="text-xs">
                        Manual SalesPersonId
                      </Label>
                      <Input
                        id="portal-salesperson-id"
                        inputMode="numeric"
                        value={manualSalesPersonId}
                        onChange={event => setManualSalesPersonId(event.target.value)}
                        placeholder={
                          selectedSalesPersonId
                            ? `Using ${selectedSalesPersonId}; enter another ID to override`
                            : 'Enter Portal SalesPersonId'
                        }
                      />
                      <div className="flex items-start justify-between gap-3">
                        <p className="text-xs text-muted-foreground">
                          {userLoadError ||
                            (portalUsers.length > 0
                              ? 'Using the Portal user captured during authentication. You can enter another numeric Portal user ID to override it.'
                              : 'Enter the numeric Portal SalesPersonId. Portal has blocked the users lookup for this API key.')}
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleTestUsersEndpoint}
                          disabled={!organization?.id || isTestingUsersEndpoint}
                          className="shrink-0"
                        >
                          {isTestingUsersEndpoint && (
                            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                          )}
                          Retry loading users
                        </Button>
                      </div>
                      {usersEndpointResult && (
                        <p className="text-xs text-muted-foreground">{usersEndpointResult}</p>
                      )}
                      {authUserFallback && (
                        <p className="text-xs text-muted-foreground">
                          Connected Portal auth user:{' '}
                          <span className="font-medium">
                            {authUserFallback.name || authUserFallback.id}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {createResult && (
                <Alert>
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertTitle>Portal draft created</AlertTitle>
                  <AlertDescription>
                    Proposal ID {createResult.proposalId} was created with{' '}
                    {createResult.createdAreas.length} area
                    {createResult.createdAreas.length === 1 ? '' : 's'} and{' '}
                    {createResult.exportedItems} product
                    {createResult.exportedItems === 1 ? '' : 's'} sent to AI Builder.
                    {createResult.aiBuilder?.buildQueued
                      ? ` AI Builder build was queued${
                          createResult.aiBuilder.buildStatus
                            ? ` (${createResult.aiBuilder.buildStatus})`
                            : ''
                        }${
                          typeof createResult.aiBuilder.observedLineItems === 'number'
                            ? `; observed ${createResult.aiBuilder.observedLineItems} Portal line item${
                                createResult.aiBuilder.observedLineItems === 1 ? '' : 's'
                              } after build polling`
                            : ''
                        }.`
                      : createResult.aiBuilder?.warning
                        ? ` AI Builder did not complete: ${createResult.aiBuilder.warning}`
                        : ''}
                    {createResult.aiBuilder?.buildQueued && createResult.aiBuilder.warning
                      ? ` ${createResult.aiBuilder.warning}`
                      : ''}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>

        {preview && (
          <div className="flex flex-col gap-2 border-t bg-background px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-muted-foreground">
              {createDisabledReason ||
                'Creates a real Portal draft, creates areas, and sends the product schedule to AI Builder.'}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={loadPreview} disabled={isLoading || isCreating}>
                Refresh preview
              </Button>
              <Button
                onClick={handleCreateProposal}
                disabled={!canCreate}
                title={createDisabledReason || undefined}
              >
                {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {!isCreating && <ExternalLink className="mr-2 h-4 w-4" />}
                Create with AI Builder
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
